/**
 * API 와 웹이 주고받는 모양
 * ---------------------------------------------------------------------------
 * DB 행이 아니라 **응답 형태**다. 암호화 컬럼(`*_enc`)은 여기에 나타나지 않고,
 * 서버가 복호화한 평문 필드로만 나온다. 클라이언트는 암호문을 볼 일이 없다.
 */

// ----------------------------------------------------------------------------
// 코드 값
// ----------------------------------------------------------------------------

/** 시설 구분. DB ck_facility_type 과 같아야 한다. */
export const FACILITY_TYPES = [
  'AIR_OUTLET',
  'AIR_PREVENTION',
  'WATER_DISCHARGE',
  'WATER_PREVENTION',
  'AUTO_MEASURE',
] as const;
export type FacilityType = (typeof FACILITY_TYPES)[number];

export const FACILITY_TYPE_LABELS: Record<FacilityType, string> = {
  AIR_OUTLET: '대기배출시설',
  AIR_PREVENTION: '대기방지시설',
  WATER_DISCHARGE: '폐수배출시설',
  WATER_PREVENTION: '수질오염방지시설',
  AUTO_MEASURE: '자동측정기기',
};

/**
 * 일지 유형. DB ck_log_type 과 같아야 한다.
 *
 * 원본 WinForm 의 LogT01/LogT02/LogT03 과 1:1 로 맞춘 이름이다. 자료를
 * 옮길 때와 법정 서식을 이야기할 때 같은 말을 쓰기 위해서다.
 */
export const LOG_TYPES = ['T01_AIR', 'T02_WATER_CONSIGN', 'T03_WATER_SELF'] as const;
export type LogType = (typeof LOG_TYPES)[number];

export const LOG_TYPE_LABELS: Record<LogType, string> = {
  T01_AIR: '대기배출시설 및 방지시설 운영기록부',
  T02_WATER_CONSIGN: '폐수배출 위탁처리 운영일지',
  T03_WATER_SELF: '폐수배출시설 자가운영일지',
};

/** 목록·탭처럼 좁은 자리에 쓰는 짧은 이름. */
export const LOG_TYPE_SHORT: Record<LogType, string> = {
  T01_AIR: '대기',
  T02_WATER_CONSIGN: '폐수 위탁',
  T03_WATER_SELF: '폐수 자가',
};

/**
 * 상단 메뉴에 쓰는 이름.
 *
 * 정식 명칭은 가로 메뉴를 혼자 다 차지하고, 짧은 이름("대기")은 무엇을 여는
 * 메뉴인지 알 수 없다. 그 사이를 쓴다.
 */
export const LOG_TYPE_MENU: Record<LogType, string> = {
  T01_AIR: '대기 운영기록부',
  T02_WATER_CONSIGN: '폐수 위탁 운영일지',
  T03_WATER_SELF: '폐수 자가 운영일지',
};

/** 일지 상태. DB ck_log_status 와 같아야 한다. */
/**
 * 일지 상태는 두 단계다.
 *
 * 임시저장을 두지 않는 이유: 법정 일지에서 "쓰다 말고 둔 것" 과 "쓴 것" 을
 * 나눠 얻는 것이 없었다. 작성자에게 [작성 완료] 를 한 번 더 누르게 했고,
 * 그 버튼을 누르지 않은 일지가 그대로 남았다. 저장하면 작성완료다.
 */
export const LOG_STATUSES = ['SUBMITTED', 'CONFIRMED'] as const;
export type LogStatus = (typeof LOG_STATUSES)[number];

export const LOG_STATUS_LABELS: Record<LogStatus, string> = {
  SUBMITTED: '작성완료',
  CONFIRMED: '확인완료',
};

/**
 * 결재 확인자 한 명.
 *
 * `seq` 는 결재란에 놓이는 순서다. 순서대로만 확인할 수 있게 하지는 않는다 —
 * 앞사람이 휴가면 결재가 멈추고, 그러면 현장은 종이로 돌아간다.
 */
export interface LogApprover {
  userId: string;
  /** 지정 시점의 이름. 퇴사 후에도 결재란은 그대로여야 한다. */
  name: string;
  seq: number;
  /** 확인한 시각. null 이면 아직 확인하지 않았다. */
  confirmedAt: string | null;
}

/** 확인자로 고를 수 있는 기업 구성원. 플랫폼이 알려 준다. */
export interface Member {
  userId: string;
  name: string;
  /** 서명 이미지를 등록해 두었는가. 없으면 결재란에 이름만 찍힌다. */
  hasSignature: boolean;
}

/** 다중 확인 결과. 한 건이 막혀도 나머지는 처리한다. */
export interface BulkConfirmResult {
  confirmed: string[];
  skipped: Array<{ dailyLogId: string; reason: string }>;
}

/** 사업장 종별. */
export const SITE_GRADES = ['1', '2', '3', '4', '5'] as const;
export type SiteGrade = (typeof SITE_GRADES)[number];

/** 일지 유형별로 어떤 시설이 필요한지. 빈 양식을 만들 때 쓴다. */
export const LOG_TYPE_FACILITY_TYPES: Record<LogType, FacilityType[]> = {
  T01_AIR: ['AIR_OUTLET', 'AIR_PREVENTION'],
  T02_WATER_CONSIGN: ['WATER_DISCHARGE'],
  T03_WATER_SELF: ['WATER_DISCHARGE', 'WATER_PREVENTION', 'AUTO_MEASURE'],
};

// ----------------------------------------------------------------------------
// 요청 문맥 (서버 내부에서도 이 모양을 쓴다)
// ----------------------------------------------------------------------------

/** 플랫폼 토큰과 제품 컨텍스트 API 에서 꺼낸, 이번 요청이 누구인지에 대한 사실. */
/**
 * 지금 값을 등록·수정할 수 있는가.
 *
 * 플랫폼이 판정해서 내려 준다(`GET /products/eco/context` 의 `access`).
 * 제품이 `subscriptionStatus` 문자열을 해석하지 않는 이유는, 해석을 제품마다
 * 하면 제품마다 다르게 막기 때문이다.
 *
 * **조회는 막지 않는다.** 운영일지는 법정 기록이라 이미 쓴 것을 못 보게 하면
 * 고객이 법정 의무를 이행할 수 없다.
 */
export interface EcoAccess {
  readable: boolean;
  writable: boolean;
  reason: 'TRIAL_EXPIRED' | 'NO_SUBSCRIPTION' | 'PAST_DUE' | 'SUSPENDED' | null;
  /** 화면에 그대로 띄울 문구. 제품이 문구를 따로 만들지 않는다 */
  message: string | null;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
}

/**
 * 기업 기본 정보. 플랫폼이 내려 준다.
 *
 * 사업장 등록 화면의 기본값으로 쓴다 — 사업장은 보통 회사와 상호·사업자번호·
 * 주소가 같아서, 받아 두면 같은 값을 두 번 적지 않아도 된다.
 */
export interface EcoCompany {
  companyName: string;
  /** 대표자명. 플랫폼이 복호화해 내려 준다. */
  ceoName: string | null;
  bizRegNo: string | null;
  bizType: string | null;
  bizItem: string | null;
  zipCode: string | null;
  address1: string | null;
  address2: string | null;
  companyTel: string | null;
}

/**
 * 기업 로고의 지문.
 *
 * 바이트는 여기 담지 않는다 — 문맥은 요청마다 오가고 로고는 수십 KB 다.
 * 화면은 이 값이 있을 때만 `GET /company/logo` 로 그림을 받아 간다.
 */
export interface EcoCompanyLogo {
  etag: string;
  contentType: string;
  updatedAt: string;
}

export interface EcoRequestContext {
  tenantId: string;
  userId: string;
  /** 로그 작성자 이름으로 스냅샷할 표시명. */
  displayName: string | null;
  companyName: string | null;
  /** 기업 기본 정보. 플랫폼이 주지 않으면 null. */
  company: EcoCompany | null;
  /** 기업 로고가 등록되어 있는가. 없으면 null — 제품 로고만 보여 준다. */
  companyLogo: EcoCompanyLogo | null;
  roles: string[];
  permissions: string[];
  /** 구독으로 열려 있는 모듈 코드. 권한과는 별개의 축이다. */
  modules: string[];
  /** 플랫폼 운영자가 지원 모드로 들어온 요청인지. 감사 기록에 남긴다. */
  supportSessionId: string | null;
  /** 등록·수정 가능 여부. 인가 가드가 쓰기 요청을 막을 근거다. */
  access: EcoAccess;
  /**
   * 이 요청의 액세스 토큰.
   *
   * 제품이 사용자를 대신해 플랫폼에 물어볼 때 쓴다(확인자로 고를 구성원
   * 명부, 서명 이미지). 제품 전용 키를 쓰지 않는 이유: 그 키는 사용량 보고
   * 전용이고, 개인정보 조회를 사용자 권한과 무관하게 열어 두면 안 된다.
   */
  accessToken: string | null;
  requestId: string;
}

// ----------------------------------------------------------------------------
// 사업장
// ----------------------------------------------------------------------------

export interface Site {
  siteId: string;
  siteCode: string;
  siteName: string;
  bizRegNo: string | null;
  permitNo: string | null;
  siteGrade: SiteGrade | null;
  bizType: string | null;
  bizItem: string | null;

  zipCode: string | null;
  address1: string | null;
  address2: string | null;
  latitude: number | null;
  longitude: number | null;

  /** 복호화된 평문. 목록 응답에서는 null 로 내려온다. */
  ceoName: string | null;
  managerName: string | null;
  managerPosition: string | null;
  technicianName: string | null;
  technicianGrade: string | null;
  contactTel: string | null;
  contactEmail: string | null;

  autoFillFacilities: boolean;
  isActive: boolean;
  openedOn: string | null;
  closedOn: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SiteInput {
  siteCode: string;
  siteName: string;
  bizRegNo?: string | null;
  permitNo?: string | null;
  siteGrade?: SiteGrade | null;
  bizType?: string | null;
  bizItem?: string | null;
  zipCode?: string | null;
  address1?: string | null;
  address2?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  ceoName?: string | null;
  managerName?: string | null;
  managerPosition?: string | null;
  technicianName?: string | null;
  technicianGrade?: string | null;
  contactTel?: string | null;
  contactEmail?: string | null;
  autoFillFacilities?: boolean;
  isActive?: boolean;
  openedOn?: string | null;
  closedOn?: string | null;
  note?: string | null;
}

// ----------------------------------------------------------------------------
// 시설
// ----------------------------------------------------------------------------

export interface Facility {
  facilityId: string;
  siteId: string;
  facilityType: FacilityType;
  outletNo: string | null;
  facilityCode: string | null;
  facilityName: string;
  installLocation: string | null;
  capacity: number | null;
  capacityUnit: string | null;
  powerCapacity: number | null;
  pollutant: string | null;
  treatmentMethod: string | null;
  installedOn: string | null;
  removedOn: string | null;
  isActive: boolean;
  sortOrder: number;
  note: string | null;
}

export interface FacilityInput {
  siteId: string;
  facilityType: FacilityType;
  outletNo?: string | null;
  facilityCode?: string | null;
  facilityName: string;
  installLocation?: string | null;
  capacity?: number | null;
  capacityUnit?: string | null;
  powerCapacity?: number | null;
  pollutant?: string | null;
  treatmentMethod?: string | null;
  installedOn?: string | null;
  removedOn?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  note?: string | null;
}

// ----------------------------------------------------------------------------
// 삭제 가능 여부
// ----------------------------------------------------------------------------

/**
 * 무엇이 삭제를 막고 있는지, 무엇이 함께 지워지는지.
 *
 * 화면이 "지울 수 없습니다" 만 말하면 사용자는 무엇을 치워야 하는지 모른다.
 * 건수를 같이 돌려주어 "일지 12건을 먼저 처리하라" 고 말할 수 있게 한다.
 */
export interface DeleteImpact {
  /** 표시용 이름 (예: '일지', '시설') */
  label: string;
  count: number;
}

export interface SiteDeleteCheck {
  deletable: boolean;
  /** 하나라도 있으면 지울 수 없다. 보존해야 하는 기록들이다 */
  blockers: DeleteImpact[];
  /** 사업장과 함께 사라지는 것들. 설정값이라 지워도 된다 */
  cascades: DeleteImpact[];
}

export interface FacilityDeleteCheck {
  deletable: boolean;
  /**
   * 이 시설을 가리키는 일지 행 수.
   *
   * 0 이 아니어도 지울 수 있다 — 일지 본문은 작성 시점의 시설명을 복사해
   * 두므로 기록 자체는 남는다. 다만 "이 시설의 지난달 가동시간" 같은
   * 조회에서는 빠지므로, 화면이 건수를 보여주고 확인을 받는다.
   */
  referencedLogCount: number;
}

// ----------------------------------------------------------------------------
// 일지 — 공통
// ----------------------------------------------------------------------------

/** 목록 행. 본문 없이 머리부만 담는다. */
export interface DailyLogSummary {
  dailyLogId: string;
  siteId: string;
  siteName: string;
  logType: LogType;
  logDate: string;
  status: LogStatus;
  weather: string | null;
  /** WMO 날씨 코드. 아이콘 판단에 쓴다. 글자를 고쳐도 이 값은 관측값이다. */
  weatherCode: number | null;
  temperature: number | null;
  writerName: string | null;
  /** 작성자의 플랫폼 사용자 ID. 결재란에 그 사람의 서명을 붙일 때 쓴다. */
  writerUserId: string | null;
  confirmerName: string | null;
  /** 확인완료를 만든 사람. 폐수 위탁 서식의 「대표자」 결재 칸에 서명을 붙인다. */
  confirmerUserId: string | null;
  confirmedAt: string | null;
  updatedAt: string;
  /**
   * 폐수 위탁 일지의 목록 요약. 목록 화면이 서식의 주요 칸을 바로 보여 준다.
   * 다른 유형이나 상세 응답에는 오지 않는다.
   */
  consign?: ConsignSummary | null;
}

/** 폐수 위탁 목록 한 줄의 요약. 단위는 서식대로 L 다. */
export interface ConsignSummary {
  uptimeStart: string | null;
  uptimeEnd: string | null;
  operatingHours: number | null;
  waterVolume: number | null;
  wastewaterGeneration: number | null;
  consignmentAmount: number | null;
  storageWastewater: number | null;
  processingCompany: string | null;
}

/** 사업장 좌표로 조회한 날씨. 못 가져오면 null 이 온다. */
export interface WeatherReading {
  weather: string;
  weatherCode: number;
  temperature: number | null;
}

/**
 * WMO 날씨 코드 → 아이콘 이름.
 *
 * 서버가 아이콘을 정하지 않는다. 코드만 내려보내고 화면이 고른다 —
 * 아이콘 세트를 바꿀 때 서버를 건드릴 이유가 없다.
 */
export type WeatherIcon = 'clear' | 'partly' | 'cloudy' | 'fog' | 'drizzle' | 'rain' | 'snow' | 'thunder';

export function weatherIcon(code: number | null | undefined): WeatherIcon | null {
  if (code === null || code === undefined) return null;
  if (code === 0) return 'clear';
  if (code === 1 || code === 2) return 'partly';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 57) return 'drizzle';
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
  if (code >= 95) return 'thunder';
  return 'cloudy';
}

/**
 * 기본 가동 시작·종료.
 *
 * 원본 WinForm 과 같은 값이다. 대부분의 사업장이 주간 1교대로 돌고, 매일
 * 같은 값을 손으로 넣게 하면 결국 아무도 정확히 적지 않는다.
 */
export const DEFAULT_UPTIME_START = '08:00';
export const DEFAULT_UPTIME_END = '18:00';

/**
 * 가동 시작·종료로 가동 시간을 계산한다.
 *
 * 종료가 시작보다 이르면 자정을 넘긴 것으로 본다. 야간 가동은 흔하고,
 * "22:00 ~ 06:00" 을 음수 시간으로 두면 집계가 전부 틀어진다.
 *
 * 서버와 화면이 같은 함수를 쓴다. 두 벌로 두면 반올림 한 자리 차이로
 * 저장할 때마다 값이 흔들린다.
 */
export function operatingHoursBetween(
  start: string | null | undefined,
  end: string | null | undefined,
): number | null {
  const from = parseHhmm(start);
  const to = parseHhmm(end);
  if (from === null || to === null) return null;

  const minutes = to >= from ? to - from : to + 24 * 60 - from;
  return Math.round((minutes / 60) * 100) / 100;
}

/** 'HH:MM' 또는 'HH:MM:SS' 를 분으로. 형식이 아니면 null. */
function parseHhmm(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return hour * 60 + minute;
}

export interface MaintenanceRow {
  logMaintenanceId?: string;
  facilityId: string | null;
  facilityName: string;
  outletNo: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  maintainer: string | null;
  details: string | null;
  sortOrder: number;
}

// ----------------------------------------------------------------------------
// T01 — 대기
// ----------------------------------------------------------------------------

export interface AirOutletRow {
  logAirOutletId?: string;
  facilityId: string | null;
  outletNo: string;
  facilityName: string;
  uptimeStart: string | null;
  uptimeEnd: string | null;
  operatingHours: number | null;
  materialName: string | null;
  materialUsage: number | null;
  materialUnit: string | null;
  note: string | null;
  sortOrder: number;
}

/**
 * 별지 제7호서식 「2. 방지시설 운영사항 — 가. 방지시설 운전사항」 한 행.
 * 칸 이름과 순서는 서식을 따른다.
 */
export interface AirPreventionRow {
  logAirPreventionId?: string;
  facilityId: string | null;
  outletNo: string | null;
  /** 방지시설명 */
  facilityName: string;
  /** 설치위치 */
  installLocation: string | null;
  /** 전력사용량(㎾/h) */
  powerConsumption: number | null;
  /** 처리용량(㎥/min). 시설의 성질이라 빈 양식을 만들 때 미리 채운다. */
  treatmentCapacity: number | null;
  /** 정격 전력용량(kW). 서식에는 없지만 전력사용량을 견줄 기준이라 남긴다. */
  powerCapacity: number | null;
  /** 처리오염물질 */
  pollutant: string | null;
  /** 처리농도(ppm 또는 ㎎/S㎥) */
  concentration: number | null;
  /** 처리효율(%) */
  efficiency: number | null;
  /** 사용약품 — 약품명 */
  drugName: string | null;
  /** 사용약품 — 사용량 */
  drugUse: number | null;
  drugUnit: string | null;
  note: string | null;
  sortOrder: number;
}

// ----------------------------------------------------------------------------
// T02 — 폐수 위탁
// ----------------------------------------------------------------------------

export interface WaterConsignBody {
  facilityId: string | null;
  facilityName: string | null;
  uptimeStart: string | null;
  uptimeEnd: string | null;
  operatingHours: number | null;
  waterVolume: number | null;
  wastewaterGeneration: number | null;
  consignmentAmount: number | null;
  storageWastewater: number | null;
  confirmationSerialNo: string | null;
  processingCompany: string | null;
  processingCompanyRegNo: string | null;
  note: string | null;
}

export interface MaterialRow {
  id?: string;
  materialName: string;
  quantity: number | null;
  unit: string | null;
  note: string | null;
  sortOrder: number;
}

// ----------------------------------------------------------------------------
// T03 — 폐수 자가운영
// ----------------------------------------------------------------------------

/*
 * 별지 제18호서식(폐수배출시설 및 수질오염방지시설 운영일지)이 정해 둔 칸들.
 *
 * 서식에 이름이 박혀 있는 행이라 빈 양식을 만들 때 그대로 깔아 준다. 사용자가
 * 지우거나 더할 수 있게 두되, 기본값이 서식과 같아야 인쇄물이 서식과 같아진다.
 */

/** 「3. 용수 공급원별 사용량과 폐수배출량」 왼쪽 — 공급원별 계량기. */
export const WATER_SUPPLY_CATEGORIES = [
  '상수도 1호',
  '상수도 2호',
  '공업용수 1호',
  '공업용수 2호',
  '지하수 1호',
  '지하수 2호',
  '하천수 1호',
  '하천수 2호',
  '해수등기타 1호',
  '해수등기타 2호',
] as const;

/** 「3. …」 오른쪽 — 배출량 및 사용량. */
export const WATER_DISCHARGE_CATEGORIES = [
  '폐수발생량',
  '폐수배출량',
  '냉각수량',
  '소모(증발량)',
  '재사용량',
  '생활용수량',
] as const;

/** 「10. 수질오염물질 측정내용」의 구분. */
export const ANALYSIS_CATEGORIES = ['원폐수', '방류수'] as const;

/** 「11. 수질자동측정기기 등의 측정항목별 점검내용」의 점검 항목. */
export const AUTO_CHECK_ITEMS = [
  '청소상태',
  '시료도입[튜빙(배관)류] 상태',
  '시약류 주입상태',
  '센서류 작동여부',
  '소모품 교체',
] as const;

/** 24시간 가동표. 배열 첫 칸이 1시다. */
export type HourFlags = boolean[];

export const EMPTY_HOURS: HourFlags = Array.from({ length: 24 }, () => false);

export interface FacilityHoursRow {
  id?: string;
  facilityId: string | null;
  facilityName: string;
  hours: HourFlags;
  note: string | null;
  sortOrder: number;
}

export interface PreventionHoursRow extends FacilityHoursRow {
  /** 운전자 성명. 복호화된 평문이다. */
  workerName: string | null;
}

export interface WaterUsageRow {
  id?: string;
  category: string;
  prevGauge: number | null;
  currGauge: number | null;
  usageAmount: number | null;
  checkTime: string | null;
  note: string | null;
  sortOrder: number;
}

export interface SludgeRow {
  id?: string;
  generationAmount: number | null;
  treatmentAmount: number | null;
  storageAmount: number | null;
  waterContent: number | null;
  storageLocation: string | null;
  selfTreatmentLocation: string | null;
  outsourcedCompany: string | null;
  note: string | null;
  sortOrder: number;
}

export interface ChemicalRow {
  id?: string;
  chemicalName: string;
  purchaseAmount: number | null;
  usageAmount: number | null;
  remainingAmount: number | null;
  unit: string | null;
  remarks: string | null;
  sortOrder: number;
}

export interface AnalysisRow {
  id?: string;
  category: string;
  phValue: number | null;
  bodValue: number | null;
  tocValue: number | null;
  codValue: number | null;
  ssValue: number | null;
  nHexValue: number | null;
  cnValue: number | null;
  cuValue: number | null;
  userDef1: number | null;
  userDef2: number | null;
  userDef3: number | null;
  userDef4: number | null;
  valueNote: string | null;
  analyzedAt: string | null;
  sortOrder: number;
}

export interface AutoCheckRow {
  id?: string;
  checkItem: string;
  phStatus: string | null;
  bodStatus: string | null;
  tocStatus: string | null;
  codStatus: string | null;
  ssStatus: string | null;
  tnStatus: string | null;
  tpStatus: string | null;
  flowStatus: string | null;
  samplerStatus: string | null;
  note: string | null;
  sortOrder: number;
}

/** 일지당 1행. 원본의 1:1 테이블 4개를 합친 것이다. */
export interface WaterSelfBody {
  /**
   * 시간대별 근무자 직함 및 성명. 서식에서 방지시설 가동표 아래 한 줄이다.
   * 여러 명이면 줄바꿈으로 나눈다. 복호화된 평문.
   */
  shiftWorkers: string | null;

  powerOperatingTime: string | null;
  powerUsageAmount: number | null;
  powerPerWater: number | null;
  powerCheckTime: string | null;
  powerTotalGauge: number | null;
  powerRemarks: string | null;

  aerationPh: number | null;
  aerationWaterTemp: number | null;
  aerationDo: number | null;
  aerationSv30: number | null;
  aerationMlss: number | null;
  aerationSvi: number | null;
  aerationTime: string | null;
  aerationMicrobeStatus: string | null;

  breakdownStatus: string | null;
  specialNotes: string | null;
  inspectionDetails: string | null;

  /** 복호화된 평문. */
  analyzerName: string | null;
  outsourcedAnalysisCompany: string | null;

  userDefTitle1: string | null;
  userDefTitle2: string | null;
  userDefTitle3: string | null;
  userDefTitle4: string | null;
}

// ----------------------------------------------------------------------------
// 일지 — 상세 / 입력
// ----------------------------------------------------------------------------

/**
 * 상세 응답. 유형에 해당하지 않는 부분은 빈 배열이나 null 로 온다.
 *
 * 유형마다 응답 형을 나누지 않는 이유: 화면이 하나의 편집기를 쓰고, 유형에
 * 따라 보여줄 구역만 바꾼다. 형을 나누면 저장·복사·상태 전이 코드를 세 벌
 * 관리하게 된다.
 */
export interface DailyLogDetail extends DailyLogSummary {
  note: string | null;

  /** 공통 */
  maintenances: MaintenanceRow[];
  /** 결재 확인자. 순서대로 온다. */
  approvers: LogApprover[];

  /** T01 */
  airOutlets: AirOutletRow[];
  airPreventions: AirPreventionRow[];

  /** T02 */
  waterConsign: WaterConsignBody | null;
  consignMaterials: MaterialRow[];

  /** T03 */
  waterSelf: WaterSelfBody | null;
  facilityHours: FacilityHoursRow[];
  preventionHours: PreventionHoursRow[];
  waterUsages: WaterUsageRow[];
  sludges: SludgeRow[];
  selfMaterials: MaterialRow[];
  chemicals: ChemicalRow[];
  analyses: AnalysisRow[];
  autoChecks: AutoCheckRow[];
}

export interface DailyLogInput {
  siteId: string;
  logType: LogType;
  logDate: string;
  weather?: string | null;
  weatherCode?: number | null;
  temperature?: number | null;
  note?: string | null;

  /** 확인자로 지정할 구성원. 순서가 결재란 순서다. */
  approverUserIds?: string[];

  maintenances?: MaintenanceRow[];

  airOutlets?: AirOutletRow[];
  airPreventions?: AirPreventionRow[];

  waterConsign?: WaterConsignBody | null;
  consignMaterials?: MaterialRow[];

  waterSelf?: WaterSelfBody | null;
  facilityHours?: FacilityHoursRow[];
  preventionHours?: PreventionHoursRow[];
  waterUsages?: WaterUsageRow[];
  sludges?: SludgeRow[];
  selfMaterials?: MaterialRow[];
  chemicals?: ChemicalRow[];
  analyses?: AnalysisRow[];
  autoChecks?: AutoCheckRow[];
}

// ----------------------------------------------------------------------------
// 복사
// ----------------------------------------------------------------------------

/** 복사한 한 건. */
export interface DailyLogCopyItem {
  logDate: string;
  dailyLogId: string;
  /** 대상일의 날씨를 실제로 받아 적용했는지. false 면 원본 값을 그대로 썼다. */
  weatherApplied: boolean;
}

/**
 * 복사하지 못한 한 건.
 *
 * 한 날짜가 실패해도 나머지는 만든다. 한 달치를 복사하다 중간에 멈추면
 * 어디까지 됐는지 사람이 확인해야 한다.
 */
export interface DailyLogCopySkip {
  logDate: string;
  /** DUPLICATE: 그날 일지가 이미 있다. FAILED: 저장 중 오류. */
  reason: 'DUPLICATE' | 'FAILED';
  message?: string;
}

export interface DailyLogCopyResult {
  created: DailyLogCopyItem[];
  skipped: DailyLogCopySkip[];
}

// ----------------------------------------------------------------------------
// 자가측정 서류
// ----------------------------------------------------------------------------

/**
 * 별지 제7호서식의 "3. 자가측정사항" 을 대신한다.
 *
 * 측정대행업자에게 전부 위탁하면 그 칸을 작성하지 않아도 되고(작성요령 1),
 * 대신 성적서를 보관한다. 인쇄할 때 한 건을 골라 붙인다.
 */
export interface SelfMeasurement {
  selfMeasurementId: string;
  siteId: string;
  siteName?: string;
  title: string;
  measuredYear: number;
  /** 1:상반기, 2:하반기. 연 1회면 null */
  half: 1 | 2 | null;
  agencyName: string | null;
  measuredOn: string | null;
  note: string | null;
  fileName: string;
  fileSize: number;
  createdAt: string;
  updatedAt: string;
}

export interface SelfMeasurementInput {
  siteId: string;
  title: string;
  measuredYear: number;
  half?: 1 | 2 | null;
  agencyName?: string | null;
  measuredOn?: string | null;
  note?: string | null;
}

// ----------------------------------------------------------------------------
// 대시보드
// ----------------------------------------------------------------------------

/** 기업별 대시보드 설정. 기업마다 쓰는 일지가 다르다. */
export interface DashboardSetting {
  /** 대시보드에 보일 일지 유형. 차트와 미작성 표시가 이것을 따른다. */
  enabledLogTypes: LogType[];
  /** 주말·공휴일도 미작성으로 칠지. 24시간 가동 사업장은 켠다. */
  countNonWorkingDays: boolean;
}

/**
 * 공휴일의 종류.
 *
 *   NATIONAL    법정공휴일 (신정·삼일절·설날·추석 등)
 *   SUBSTITUTE  대체공휴일 (규칙으로 계산)
 *   TEMPORARY   임시공휴일 (정부 지정, 표로 관리)
 *   ELECTION    선거일
 */
export type HolidayKind = 'NATIONAL' | 'SUBSTITUTE' | 'TEMPORARY' | 'ELECTION';

export interface Holiday {
  date: string;
  name: string;
  kind: HolidayKind;
}

/** 달력 한 칸에 표시할 일지 한 건. */
export interface CalendarLogMark {
  dailyLogId: string;
  logType: LogType;
  status: LogStatus;
}

/** 달력 한 칸. */
export interface CalendarDay {
  date: string;
  /** 공휴일이면 이름. 같은 날 둘이 겹치면(2025-05-05) 이름을 이어 붙인다. */
  holidays: Holiday[];
  /** 토·일·공휴일인가. 미작성 판정에서 뺄 수 있다. */
  nonWorking: boolean;
  logs: CalendarLogMark[];
  /** 써야 하는데 아직 안 쓴 유형. 오늘 이후는 비어 있다. */
  missing: LogType[];
}

export interface CalendarMonth {
  year: number;
  month: number;
  days: CalendarDay[];
  setting: DashboardSetting;
}

/** 차트 한 점. 날짜별 값이다. */
export interface ChartPoint {
  date: string;
  [series: string]: number | string | null;
}

/**
 * 차트 한 장.
 *
 * `series` 는 선·막대 하나하나의 이름이다. 배출구별·시설별처럼 개수가
 * 기업마다 달라 고정할 수 없다. 단위가 다른 값을 한 축에 섞지 않도록
 * 차트마다 단위를 하나만 둔다 — 원료처럼 단위가 여럿이면 단위별로 나눈다.
 */
export interface DashboardChart {
  key: string;
  title: string;
  unit: string;
  /** line: 추이를 보는 값, bar: 하루 합계를 견주는 값. */
  kind: 'line' | 'bar';
  series: string[];
  points: ChartPoint[];
}

export interface DashboardCharts {
  from: string;
  to: string;
  byType: Partial<Record<LogType, DashboardChart[]>>;
}

// ----------------------------------------------------------------------------
// 공통 응답
// ----------------------------------------------------------------------------

export interface PageResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ApiError {
  code?: string;
  message: string;
  details?: Record<string, string[]>;
  requestId?: string;
}
